import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Tag,
  Button,
  Select,
  Space,
  Typography,
  Empty,
  DatePicker,
} from 'antd';
import {
  PlusOutlined,
  OrderedListOutlined,
  ClockCircleOutlined,
  FileDoneOutlined,
  BankOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatMontant, formatDate } from '../../variables/constants';
import TableExportButton from '../../components/TableExportButton';
import logoLocal from '../../assets/images/logo.png';
import PageHeader from '../../components/PageHeader';
import { pourcent } from '../../services/tauxRetenueService';
import { filtreSansAccents } from '../../utils/rechercheTexte';
import { useTranslation } from 'react-i18next';
import { localeIntl } from '../../i18n/format';

/* LES LIBELLES DES STATUTS VIVAIENT EN DOUBLE, ici et dans le detail d'un lot. Ils sont
   desormais au meme endroit : deux ecrans qui nomment differemment le meme etat se
   contredisent sous les yeux de l'utilisateur. */
import { LIBELLE_STATUT_LOT, libelleStatutLot, libelleStatutPaiement } from './libellesStatut';

const { Option } = Select;

// Construit au rendu : la valeur vide (« tous ») n'a pas de code a traduire.
/* LE FILTRE ET LA PASTILLE NOMMAIENT DIFFEREMMENT LE MEME ETAT. Ce filtre disait
   « Validation technique » la ou la pastille du tableau dit « Technique valide » - sur le
   meme ecran, pour le meme statut. Les deux puisent desormais dans la meme table, et
   l'ordre des choix suit celui du circuit. */
const statutsFiltre = (t) => [
  { value: '', label: t('liste.statuts.tous') },
  ...Object.keys(LIBELLE_STATUT_LOT).map((code) => ({
    value: code,
    label: LIBELLE_STATUT_LOT[code],
  })),
];

// `get label()` : le libelle se lit au rendu, dans la langue de l'ecran, et non au
// chargement du module.
const STATUT_LOT_DISPLAY = {
  EMIS: {
    color: 'default',
    icon: <ClockCircleOutlined />,
    get label() { return LIBELLE_STATUT_LOT.EMIS; },
  },
  REGLEMENT_TECHNIQUE_VALIDE: {
    color: 'processing',
    icon: <FileDoneOutlined />,
    get label() { return LIBELLE_STATUT_LOT.REGLEMENT_TECHNIQUE_VALIDE; },
  },
  REGLEMENT_COMPTABLE_VALIDE: {
    color: 'cyan',
    icon: <BankOutlined />,
    get label() { return LIBELLE_STATUT_LOT.REGLEMENT_COMPTABLE_VALIDE; },
  },
  PAYE: {
    color: 'success',
    icon: <CheckCircleOutlined />,
    get label() { return LIBELLE_STATUT_LOT.PAYE; },
  },
  PARTIELLEMENT_ANNULE: {
    color: 'warning',
    icon: <ExclamationCircleOutlined />,
    get label() { return LIBELLE_STATUT_LOT.PARTIELLEMENT_ANNULE; },
  },
  ANNULE: {
    color: 'error',
    icon: <ExclamationCircleOutlined />,
    get label() { return LIBELLE_STATUT_LOT.ANNULE; },
  },
};

const PAGE_SIZE = 20;

const extractList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.content)) return data.content;
  return [];
};

export default function LotReglementsListe() {
  const navigate = useNavigate();
  const { t } = useTranslation('reglementsExperts');
  const STATUTS = statutsFiltre(t);
  const [filtreStatut, setFiltreStatut] = useState('');
  const [filtreExpertId, setFiltreExpertId] = useState(null);
  const [dateDebut, setDateDebut] = useState(null);
  const [dateFin, setDateFin] = useState(null);
  const [page, setPage] = useState(1);
  const [selectedLotKeys, setSelectedLotKeys] = useState([]);

  //  Experts présents dans les lots (dédoublonnés depuis /v1/lot-reglements) 
  const { data: allLotsData } = useQuery({
    queryKey: ['lot-reglements-all-experts'],
    queryFn: () =>
      axiosInstance.get('/v1/lot-reglements', { params: { page: 0, size: 500 } }),
    staleTime: 5 * 60 * 1000, // 5 min - données stables pour le filtre
  });
  const allLotsRaw = allLotsData?.data?.data ?? allLotsData?.data ?? null;
  const allLots = extractList(allLotsRaw?.content ?? allLotsRaw);
  const experts = Object.values(
    allLots.reduce((acc, lot) => {
      if (lot.expertTrackingId && !acc[lot.expertTrackingId]) {
        acc[lot.expertTrackingId] = {
          expertTrackingId: lot.expertTrackingId,
          expertNomComplet: lot.expertNomComplet || '',
        };
      }
      return acc;
    }, {})
  ).sort((a, b) => a.expertNomComplet.localeCompare(b.expertNomComplet, 'fr'));

  const { data, isLoading, isError } = useQuery({
    queryKey: ['lot-reglements', filtreStatut, filtreExpertId, dateDebut, dateFin, page],
    queryFn: () =>
      axiosInstance.get('/v1/lot-reglements', {
        params: {
          statut: filtreStatut || undefined,
          expertTrackingId: filtreExpertId || undefined,
          dateDebut: dateDebut ? dateDebut.format('YYYY-MM-DD') : undefined,
          dateFin: dateFin ? dateFin.format('YYYY-MM-DD') : undefined,
          page: page - 1,
          size: PAGE_SIZE,
        },
      }),
    keepPreviousData: true,
  });

  const responseData = data?.data?.data ?? data?.data ?? null;
  const lots = extractList(responseData?.content ?? responseData);
  const totalElements = responseData?.totalElements ?? lots.length;

  const hasActiveFilters = !!(filtreStatut || filtreExpertId || dateDebut || dateFin);

  const resetFilters = () => {
    setFiltreStatut('');
    setFiltreExpertId(null);
    setDateDebut(null);
    setDateFin(null);
    setPage(1);
  };

  const columns = [
    {
      title: t('liste.colonnes.idLot'),
      dataIndex: 'lotTrackingId',
      key: 'lotTrackingId',
      width: 110,
      render: (v) => (
        <span style={{ fontFamily: 'var(--font-main)', fontWeight: 700, color: '#1a6b3c' }}>
          {v?.slice(0, 8).toUpperCase()}
        </span>
      ),
    },
    {
      title: t('liste.colonnes.expert'),
      dataIndex: 'expertNomComplet',
      key: 'expert',
      ellipsis: true,
      render: (v) => v ?? '-',
    },
    {
      title: t('liste.colonnes.creeLe'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (v) => formatDate(v),
    },
    {
      title: t('liste.colonnes.tauxRetenue'),
      dataIndex: 'tauxRetenueValeur',
      key: 'tauxRetenue',
      width: 120,
      // Le taux APPLIQUÉ au lot, figé à sa création — pas le taux actuel du
      // référentiel, qui a pu changer depuis.
      render: (v, lot) => (
        <Tag style={{ borderRadius: 20 }}>
          {v != null ? pourcent(v) : (lot.tauxRetenue ?? '-')}
        </Tag>
      ),
    },
    {
      title: t('liste.colonnes.nbReglements'),
      dataIndex: 'nombreReglements',
      key: 'nombreReglements',
      width: 120,
      align: 'center',
      render: (v) => v ?? '-',
    },
    {
      title: t('liste.colonnes.totalTtc'),
      dataIndex: 'montantTtcTotal',
      key: 'montantTtcTotal',
      width: 140,
      align: 'right',
      render: (v) => (
        <span style={{ fontWeight: 700, color: '#1a6b3c' }}>
          {formatMontant(v)}
        </span>
      ),
    },
    {
      title: t('liste.colonnes.statut'),
      dataIndex: 'statut',
      key: 'statut',
      width: 160,
      render: (v) => {
        const display = STATUT_LOT_DISPLAY[v]
          ?? { color: 'default', icon: null, label: libelleStatutLot(v) };
        return (
          <Tag color={display.color} icon={display.icon} style={{ borderRadius: 20 }}>
            {display.label}
          </Tag>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          style={{ color: '#1a6b3c', padding: 0, fontWeight: 500 }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/lot-reglements/${record.lotTrackingId}`);
          }}
        >
          {t('liste.detail')}
        </Button>
      ),
    },
  ];

  //  Export 

  const exportColumns = [
    { title: t('liste.export.type'), dataIndex: 'type' },
    { title: t('liste.export.reference'), dataIndex: 'reference' },
    { title: t('liste.export.expertOuBeneficiaire'), dataIndex: 'expertOuBeneficiaire' },
    { title: t('liste.export.date'), dataIndex: 'date' },
    { title: t('liste.export.statut'), dataIndex: 'statut' },
    { title: t('liste.export.montant'), dataIndex: 'montant', format: 'currency', numeric: true },
  ];

  const buildExportRows = async () => {
    const targets = selectedLotKeys.length
      ? lots.filter((l) => selectedLotKeys.includes(l.lotTrackingId))
      : lots;
    if (!targets.length) return [];
    const details = await Promise.all(targets.map((l) =>
      axiosInstance.get(`/v1/lot-reglements/${l.lotTrackingId}`).then(r => r.data?.data)
    ));
    const rows = [];
    targets.forEach((lot, idx) => {
      const detail = details[idx];
      const lotStatutLabel = lot.statut ? libelleStatutLot(lot.statut) : '';
      rows.push({
        type: t('liste.export.typeLot'),
        reference: lot.numeroLot || lot.lotTrackingId?.slice(0, 8).toUpperCase() || '',
        expertOuBeneficiaire: lot.expertNomComplet || '',
        date: lot.createdAt ? new Date(lot.createdAt).toLocaleDateString(localeIntl()) : '',
        statut: lotStatutLabel,
        montant: lot.montantTtcTotal ?? '',
      });
      const paiements = detail?.paiements || [];
      paiements.forEach((p) => {
        // Un statut de PAIEMENT : il etait lu dans la table des LOTS. Les deux enums se
        // recouvrent sur cinq valeurs, donc cela marchait - par coincidence.
        const pStatutLabel = p.statut ? libelleStatutPaiement(p.statut) : '';
        rows.push({
          type: `  ${t('liste.export.typeReglement')}`,
          reference: p.numeroPaiement || '',
          expertOuBeneficiaire: p.beneficiaire || '',
          date: p.dateEmission ? new Date(p.dateEmission).toLocaleDateString(localeIntl()) : '',
          statut: pStatutLabel,
          montant: p.montantTtc ?? '',
        });
      });
    });
    return rows;
  };


  return (
    <Layout title={t('liste.titre')}>

      {/* En-tête page */}
      {/* Pagination et filtres sont cote serveur : le compte vient de
          `totalElements`, pas de la page courante. */}
      <PageHeader
        titre={t('liste.titre')}
        icone={<OrderedListOutlined />}
        compte={totalElements}
        unite={t('liste.unite')}
        unitePluriel={t('liste.unitePluriel')}
        actions={(
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/reglements-multiple-experts')}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8, fontWeight: 600 }}
          >
            {t('liste.nouveauLot')}
          </Button>
        )}
      />

      {/* Filtres */}
      <div className="cb-card" style={{ marginBottom: 16 }}>
        <Space wrap align="center" style={{ marginBottom: 0, width: '100%' }}>
          <Space size="small">
            <FilterOutlined style={{ color: '#1a6b3c' }} />
            <Typography.Text style={{ color: '#374151', fontWeight: 500 }}>{t('liste.filtres')}</Typography.Text>
          </Space>
          <Select
            value={filtreStatut}
            onChange={(val) => { setFiltreStatut(val); setPage(1); }}
            style={{ width: 220, borderRadius: 8 }}
            placeholder={t('liste.statuts.tous')}
          >
            {STATUTS.map((s) => (
              <Option key={s.value} value={s.value}>
                {s.label}
              </Option>
            ))}
          </Select>
          <Select
            showSearch
            filterOption={filtreSansAccents}
            allowClear
            placeholder={t('liste.filtrerExpert')}
            value={filtreExpertId}
            onChange={(v) => { setFiltreExpertId(v ?? null); setPage(1); }}
            options={experts.map(e => ({
              value: e.expertTrackingId,
              label: e.expertNomComplet,
            }))}
            optionFilterProp="label"
            style={{ minWidth: 240 }}
          />
          <DatePicker
            placeholder={t('liste.dateDebut')}
            value={dateDebut}
            onChange={(d) => { setDateDebut(d); setPage(1); }}
            format="DD/MM/YYYY"
          />
          <DatePicker
            placeholder={t('liste.dateFin')}
            value={dateFin}
            onChange={(d) => { setDateFin(d); setPage(1); }}
            format="DD/MM/YYYY"
          />
          {hasActiveFilters && (
            <Button onClick={resetFilters} danger>{t('liste.reinitialiser')}</Button>
          )}
          <Typography.Text style={{ color: '#6b7280', fontSize: 13 }}>
            {t(totalElements !== 1 ? 'liste.lotsTrouves' : 'liste.lotTrouve', { n: totalElements })}
          </Typography.Text>
        </Space>
      </div>

      {/* Table */}
      <div className="cb-card" style={{ padding: 0 }}>
        <Table
          className="cb-table"
          rowKey="lotTrackingId"
          rowSelection={{
            selectedRowKeys: selectedLotKeys,
            onChange: setSelectedLotKeys,
          }}
          columns={columns}
          dataSource={lots}
          loading={isLoading}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span style={{ color: '#6b7280' }}>
                    {isError
                      ? t('liste.erreurChargement')
                      : t('liste.aucunLot')}
                  </span>
                }
              />
            ),
          }}
          onRow={(record) => ({
            style: { cursor: 'pointer' },
            onClick: () => navigate(`/lot-reglements/${record.lotTrackingId}`),
          })}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: totalElements,
            showSizeChanger: false,
            onChange: (p) => setPage(p),
          }}
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </div>

      {/* Bouton flottant export bas-droite */}
      <TableExportButton
        floating
        dataLoader={buildExportRows}
        columns={exportColumns}
        filename={`lots_reglements_${new Date().toISOString().slice(0, 10)}`}
        title={t('liste.export.titre')}
        subtitle={(() => {
          const parts = [];
          if (filtreExpertId) {
            const exp = experts.find(e => e.expertTrackingId === filtreExpertId);
            if (exp) parts.push(t('liste.export.filtreExpert', { nom: exp.expertNomComplet || exp.nomComplet || '' }));
          }
          if (filtreStatut) parts.push(t('liste.export.filtreStatut', { statut: STATUTS.find(s => s.value === filtreStatut)?.label || filtreStatut }));
          if (dateDebut || dateFin) parts.push(t('liste.export.filtrePeriode', { debut: dateDebut ? dateDebut.format('DD/MM/YYYY') : '...', fin: dateFin ? dateFin.format('DD/MM/YYYY') : '...' }));
          return parts.join(' · ');
        })()}
        count={selectedLotKeys.length || lots.length}
        logo={logoLocal}
        showTotals={false}
      />

    </Layout>
  );
}
